import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { hasPermission as checkPermission } from '../constants/permissions';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * iOS Safari / PostHog defence — fetch a fresh native fetch from a
 * sandbox iframe, bypassing any wrapper (PostHog session-recording,
 * Sentry, etc.) that may have re-wrapped window.fetch since page load.
 *
 * Called right before auth-critical requests so login never trips the
 * "no global object" / "body stream already read" wrapper bug.
 *
 * Returns native fetch bound to window, or falls back to window.fetch
 * if the iframe trick fails.
 */
const getCleanFetch = () => {
  try {
    if (typeof document === 'undefined') return window.fetch.bind(window);
    const sandbox = document.createElement('iframe');
    sandbox.setAttribute('aria-hidden', 'true');
    sandbox.style.display = 'none';
    (document.documentElement || document.body).appendChild(sandbox);
    const nativeFetch = sandbox.contentWindow && sandbox.contentWindow.fetch;
    sandbox.parentNode.removeChild(sandbox);
    if (typeof nativeFetch === 'function') return nativeFetch.bind(window);
  } catch (_) {
    /* fall through */
  }
  return window.fetch.bind(window);
};

/**
 * Translate opaque platform errors (iOS WebKit's "no global object",
 * fetch failures, etc.) into a friendly, actionable message.
 */
const friendlyAuthError = (err) => {
  const msg = (err && err.message) || '';
  if (!msg) return 'Login failed. Please try again.';
  // iOS Safari / PostHog wrapper noise — never useful to users.
  if (/no global object|body stream already read/i.test(msg)) {
    return 'Connection hiccup. Please pull-to-refresh the page and try again.';
  }
  return msg;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem('token'));

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    // Tolerant /me probe — a SINGLE transient 401 (server restart, race
    // on token persistence, brief network blip on mobile data) used to
    // nuke the user's token immediately and bounce them to the login
    // screen. That's the "logged out by itself sometime" complaint.
    // Fix: try up to 3 times with a small backoff before giving up.
    const probe = async (authHeader) => {
      const opts = { credentials: 'include' };
      if (authHeader) opts.headers = { Authorization: authHeader };
      try {
        return await fetch(`${API_URL}/api/auth/me`, opts);
      } catch (_) {
        return null;
      }
    };

    const tryWithRetry = async (authHeader) => {
      for (let attempt = 0; attempt < 3; attempt++) {
        const r = await probe(authHeader);
        // Success → return immediately
        if (r && r.ok) return r;
        // 401/403 explicitly mean "the token is invalid" — no point
        // retrying those, the server has decided.
        //
        // PERF FIX (Iter 114): return the 401 IMMEDIATELY on the first
        // attempt instead of retrying 3× with 600/1200ms backoff.
        // The retry loop was originally added for transient mobile
        // network blips, but for a 401 the answer is already final.
        // Wasting 1.8s + 3 extra round-trips on every cold start
        // before showing the login form is the single biggest reason
        // the Login page felt slow in production.
        if (r && (r.status === 401 || r.status === 403)) {
          return r;
        }
        // Other error / network failure → backoff and retry
        await new Promise((res) => setTimeout(res, 600 * (attempt + 1)));
      }
      return null;
    };

    if (token) {
      const response = await tryWithRetry(`Bearer ${token}`);
      if (response && response.ok) {
        try {
          const userData = await response.json();
          setUser(userData);
        } catch (_) { /* ignore */ }
      } else if (response && response.status === 401) {
        // Confirmed invalid token after 3 attempts → log out cleanly.
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
      }
      // If response is null (all 3 attempts threw network errors), we
      // KEEP the token and the existing user state. The user can keep
      // working; the next API call or `checkAuth` cycle will retry.
    } else {
      // Try cookie-based auth (Emergent OAuth path)
      const response = await tryWithRetry(null);
      if (response && response.ok) {
        try {
          const userData = await response.json();
          setUser(userData);
        } catch (_) { /* ignore */ }
      }
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Global "session expired" listener — `handleResponse` in api.js
  // broadcasts a `tsp:session-expired` event the FIRST time any API
  // call returns 401 with a real token in localStorage. We swallow
  // duplicate events (debounced to once every 5s) and route the user
  // to /login cleanly without nuking unsaved form state mid-click.
  useEffect(() => {
    let lastBroadcast = 0;
    const onSessionExpired = () => {
      const now = Date.now();
      if (now - lastBroadcast < 5000) return;
      lastBroadcast = now;
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
      // Don't hard-redirect — let the ProtectedRoute send them to
      // /login. That way the URL preserves their intended destination
      // for a post-login bounce-back later.
    };
    window.addEventListener('tsp:session-expired', onSessionExpired);
    return () => window.removeEventListener('tsp:session-expired', onSessionExpired);
  }, []);

  const login = async (username, password) => {
    // Use a fresh native fetch (bypass any PostHog/analytics fetch wrapper
    // installed after page load — iOS Safari throws "no global object" via
    // those wrappers).
    const cleanFetch = getCleanFetch();

    let response;
    try {
      response = await cleanFetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });
    } catch (e) {
      throw new Error(friendlyAuthError(e));
    }

    // Read body as text first, then parse to avoid "body stream already read" error
    const responseText = await response.text();
    let data;
    try {
      data = JSON.parse(responseText);
    } catch {
      throw new Error('Login failed');
    }
    
    if (!response.ok) {
      throw new Error(data.detail || 'Login failed');
    }
    
    localStorage.setItem('token', data.token);
    setToken(data.token);
    setUser(data.user);
    return data;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const processOAuthSession = async (sessionId) => {
    const response = await fetch(`${API_URL}/api/auth/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: sessionId }),
      credentials: 'include'
    });
    
    if (!response.ok) {
      throw new Error('OAuth session processing failed');
    }
    
    const data = await response.json();
    setUser(data.user);
    return data;
  };

  const logout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const value = {
    user,
    token,
    loading,
    login,
    loginWithGoogle,
    processOAuthSession,
    logout,
    isAdmin: user?.role === 'admin',
    isAuthenticated: !!user,
    hasPermission: (moduleKey, action) => checkPermission(user, moduleKey, action)
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
